import { Platform, ColumnMappingResult, PlatformDetectionResult } from '@/types'

// Import mapping configurations
import boltMappings from '@/config/columnMappings/boltMappings.json'
import uberMappings from '@/config/columnMappings/uberMappings.json'
import sumupMappings from '@/config/columnMappings/sumupMappings.json'
import genericMappings from '@/config/columnMappings/genericMappings.json'
import driverImportMappings from '@/config/columnMappings/driverImportMappings.json'

/**
 * Column Mapping Service
 *
 * Responsible for mapping CSV headers to internal field names based on platform configuration.
 * Works with results from PlatformDetectionService.
 */

// Configuration type for mappings
export interface ColumnMappingConfig {
  platform: string
  identifierFields: Record<string, string[]>
  earningsFields: Record<string, string[]>
  profileFields?: Record<string, string[]>
  requiredFields: {
    identifierFields: string[]
    earningsFields: string[]
    profileFields?: string[]
  }
  optionalFields: {
    identifierFields: string[]
    earningsFields: string[]
    profileFields?: string[]
  }
}

export interface NormalizedHeader {
  original: string
  normalized: string
  tokens: string[]
}

export interface FieldMatch {
  internalField: string
  headerName: string
  confidence: number
  reason: string
}

export class ColumnMappingService {
  private static mappingConfigs: Record<string, ColumnMappingConfig> = {
    bolt: boltMappings as ColumnMappingConfig,
    uber: uberMappings as ColumnMappingConfig,
    sumup: sumupMappings as ColumnMappingConfig,
    generic: genericMappings as ColumnMappingConfig,
    driver_import: driverImportMappings as ColumnMappingConfig
  }

  /**
   * Get configuration for a specific platform
   */
  static getConfigForPlatform(platform: string): ColumnMappingConfig | null {
    const configKey = platform === 'other' ? 'generic' : platform
    return this.mappingConfigs[configKey] || null
  }

  /**
   * Create column mapping based on detected platform
   */
  static createColumnMapping(
    headers: string[],
    platformInfo: PlatformDetectionResult,
    platformHint?: Platform
  ): ColumnMappingResult {
    // Choose the best platform configuration
    const targetPlatform = platformHint || platformInfo.detectedPlatform || 'generic'
    const configKey = targetPlatform === 'other' ? 'generic' : targetPlatform

    if (!this.mappingConfigs[configKey]) {
      console.warn(`[ColumnMapping] Unknown platform: ${configKey}, falling back to generic`)
      return this.mapColumnsForPlatform(headers, this.mappingConfigs.generic)
    }

    return this.mapColumnsForPlatform(headers, this.mappingConfigs[configKey])
  }

  /**
   * Test all platforms and return best mapping (for fallback/comparison)
   */
  static detectBestColumnMapping(headers: string[]): {
    bestMapping: ColumnMappingResult
    allMappings: Array<{
      platform: string
      confidence: number
      mapping: ColumnMappingResult
    }>
  } {
    const platformMappings: Array<{
      platform: string
      confidence: number
      mapping: ColumnMappingResult
    }> = []

    // Test all platform configurations
    for (const [platform, config] of Object.entries(this.mappingConfigs)) {
      const mapping = this.mapColumnsForPlatform(headers, config)
      platformMappings.push({
        platform,
        confidence: mapping.confidence,
        mapping
      })
    }

    // Sort by confidence
    platformMappings.sort((a, b) => b.confidence - a.confidence)

    const bestMapping = platformMappings[0]?.mapping || {
      identifierFields: {},
      earningsFields: {},
      profileFields: {},
      confidence: 0,
      manualOverride: false,
      unmappedHeaders: headers,
      missingRequiredFields: []
    }

    return {
      bestMapping,
      allMappings: platformMappings
    }
  }

  /**
   * Map columns for a specific platform configuration
   */
  private static mapColumnsForPlatform(
    headers: string[],
    config: ColumnMappingConfig
  ): ColumnMappingResult {
    const normalizedHeaders = headers.map(h => this.normalizeHeader(h))

    const identifierFields: Record<string, string> = {}
    const earningsFields: Record<string, string> = {}
    const profileFields: Record<string, string> = {}
    const unmappedHeaders: string[] = []
    const missingRequiredFields: string[] = []

    let totalScore = 0
    let maxPossibleScore = 0

    // Map identifier fields
    for (const [fieldKey, synonyms] of Object.entries(config.identifierFields)) {
      const isRequired = config.requiredFields.identifierFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxPossibleScore += weight

      let bestMatch = { headerIndex: -1, confidence: 0, reason: '' }

      // Test each header
      for (let i = 0; i < normalizedHeaders.length; i++) {
        const match = this.findBestFieldMatch(normalizedHeaders[i], synonyms)
        if (match.confidence > bestMatch.confidence) {
          bestMatch = { headerIndex: i, ...match }
        }
      }

      // If good match found, use it
      if (bestMatch.confidence >= 0.6) {
        identifierFields[fieldKey] = headers[bestMatch.headerIndex]
        totalScore += weight * bestMatch.confidence
      } else if (isRequired) {
        missingRequiredFields.push(fieldKey)
      }
    }

    // Check for alternative driver name requirement satisfaction
    // If driverName is missing but firstName and lastName are both mapped, remove driverName from missing required fields
    if (missingRequiredFields.includes('driverName') &&
        identifierFields.firstName &&
        identifierFields.lastName) {
      const driverNameIndex = missingRequiredFields.indexOf('driverName')
      missingRequiredFields.splice(driverNameIndex, 1)

      // Add score for the satisfied requirement
      const driverNameWeight = 2
      totalScore += driverNameWeight * 0.9
    }

    // Map earnings fields
    for (const [fieldKey, synonyms] of Object.entries(config.earningsFields)) {
      const isRequired = config.requiredFields.earningsFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxPossibleScore += weight

      let bestMatch = { headerIndex: -1, confidence: 0, reason: '' }

      // Test each header
      for (let i = 0; i < normalizedHeaders.length; i++) {
        // Skip if already used by identifier fields
        if (Object.values(identifierFields).includes(headers[i])) continue

        const match = this.findBestFieldMatch(normalizedHeaders[i], synonyms)
        if (match.confidence > bestMatch.confidence) {
          bestMatch = { headerIndex: i, ...match }
        }
      }

      // If good match found, use it
      if (bestMatch.confidence >= 0.6) {
        earningsFields[fieldKey] = headers[bestMatch.headerIndex]
        totalScore += weight * bestMatch.confidence
      } else if (isRequired) {
        missingRequiredFields.push(fieldKey)
      }
    }

    // Map profile fields (driver master specific fields)
    const profileFieldConfig = config.profileFields || {}
    const requiredProfileFields = config.requiredFields.profileFields || []

    for (const [fieldKey, synonyms] of Object.entries(profileFieldConfig)) {
      const isRequired = requiredProfileFields.includes(fieldKey)
      const weight = isRequired ? 2 : 1
      maxPossibleScore += weight

      let bestMatch = { headerIndex: -1, confidence: 0, reason: '' }

      for (let i = 0; i < normalizedHeaders.length; i++) {
        // Skip if header already mapped to identifier or earnings fields
        if (
          Object.values(identifierFields).includes(headers[i]) ||
          Object.values(earningsFields).includes(headers[i])
        ) {
          continue
        }

        const match = this.findBestFieldMatch(normalizedHeaders[i], synonyms)
        if (match.confidence > bestMatch.confidence) {
          bestMatch = { headerIndex: i, ...match }
        }
      }

      if (bestMatch.confidence >= 0.6) {
        profileFields[fieldKey] = headers[bestMatch.headerIndex]
        totalScore += weight * bestMatch.confidence
      } else if (isRequired) {
        missingRequiredFields.push(fieldKey)
      }
    }

    // Find unmapped headers
    const mappedHeaders = [
      ...Object.values(identifierFields),
      ...Object.values(earningsFields),
      ...Object.values(profileFields)
    ]

    headers.forEach(header => {
      if (!mappedHeaders.includes(header)) {
        unmappedHeaders.push(header)
      }
    })

    // Calculate confidence score
    const baseConfidence = maxPossibleScore > 0 ? (totalScore / maxPossibleScore) * 100 : 0

    // Apply penalties for missing required fields
    const requiredFieldPenalty = missingRequiredFields.length * 15
    const finalConfidence = Math.max(0, Math.round(baseConfidence - requiredFieldPenalty))

    return {
      identifierFields,
      earningsFields,
      profileFields,
      confidence: finalConfidence,
      manualOverride: false,
      unmappedHeaders,
      missingRequiredFields
    }
  }

  /**
   * Get manual mapping options for user override
   */
  static getManualMappingOptions(
    headers: string[],
    currentMapping: ColumnMappingResult,
    platformName: string = 'generic'
  ): {
    availableHeaders: string[]
    unmappedIdentifierFields: string[]
    unmappedEarningsFields: string[]
    unmappedProfileFields: string[]
    suggestions: Record<string, string[]>
  } {
    const mappedHeaders = [
      ...Object.values(currentMapping.identifierFields),
      ...Object.values(currentMapping.earningsFields),
      ...Object.values(currentMapping.profileFields || {})
    ]

    const availableHeaders = headers.filter(h => !mappedHeaders.includes(h))

    // Find unmapped fields from platform configuration
    const configKey = platformName === 'other' ? 'generic' : platformName
    const platformConfig = this.mappingConfigs[configKey] || this.mappingConfigs.generic

    const allIdentifierFields = Object.keys(platformConfig.identifierFields)
    const allEarningsFields = Object.keys(platformConfig.earningsFields)
    const allProfileFields = Object.keys(platformConfig.profileFields || {})

    const unmappedIdentifierFields = allIdentifierFields.filter(
      field => !currentMapping.identifierFields[field]
    )
    const unmappedEarningsFields = allEarningsFields.filter(
      field => !currentMapping.earningsFields[field]
    )
    const unmappedProfileFields = allProfileFields.filter(
      field => !(currentMapping.profileFields || {})[field]
    )

    // Generate suggestions (simplified for now)
    const suggestions: Record<string, string[]> = {}

    return {
      availableHeaders,
      unmappedIdentifierFields,
      unmappedEarningsFields,
      unmappedProfileFields,
      suggestions
    }
  }

  /**
   * Create manual mapping override
   */
  static createManualMapping(
    headers: string[],
    identifierFieldMapping: Record<string, string>,
    earningsFieldMapping: Record<string, string> = {},
    profileFieldMapping: Record<string, string> = {}
  ): ColumnMappingResult {
    // Validate that all mapped headers actually exist
    const allMappedHeaders = [
      ...Object.values(identifierFieldMapping),
      ...Object.values(earningsFieldMapping),
      ...Object.values(profileFieldMapping)
    ]
    const invalidHeaders = allMappedHeaders.filter(header => !headers.includes(header))

    if (invalidHeaders.length > 0) {
      throw new Error(`Invalid headers in mapping: ${invalidHeaders.join(', ')}`)
    }

    // Find unmapped headers
    const unmappedHeaders = headers.filter(header => !allMappedHeaders.includes(header))

    return {
      identifierFields: identifierFieldMapping,
      earningsFields: earningsFieldMapping,
      profileFields: profileFieldMapping,
      confidence: 100, // Manual mapping gets full confidence
      manualOverride: true,
      unmappedHeaders,
      missingRequiredFields: [] // User is responsible for required fields in manual mapping
    }
  }

  /**
   * Find best match for a header against field synonyms
   */
  private static findBestFieldMatch(
    normalizedHeader: NormalizedHeader,
    fieldSynonyms: string[]
  ): { confidence: number; reason: string } {
    let bestConfidence = 0
    let bestReason = ''

    for (const synonym of fieldSynonyms) {
      const normalizedSynonym = this.normalizeHeader(synonym)

      // Exact match
      if (normalizedHeader.normalized === normalizedSynonym.normalized) {
        return { confidence: 1.0, reason: `Exact match: "${synonym}"` }
      }

      // String similarity
      const similarity = this.calculateSimilarity(
        normalizedHeader.normalized,
        normalizedSynonym.normalized
      )

      if (similarity > bestConfidence && similarity > 0.7) {
        bestConfidence = similarity
        bestReason = `Similar to: "${synonym}" (${Math.round(similarity * 100)}%)`
      }

      // Token-based matching
      const tokenMatch = this.calculateTokenMatch(
        normalizedHeader.tokens,
        normalizedSynonym.tokens
      )

      if (tokenMatch > bestConfidence && tokenMatch > 0.6) {
        bestConfidence = tokenMatch
        bestReason = `Token match: "${synonym}" (${Math.round(tokenMatch * 100)}%)`
      }

      // Substring matching for shorter synonyms
      if (synonym.length >= 3) {
        const isSubstring = normalizedHeader.normalized.includes(normalizedSynonym.normalized) ||
                           normalizedSynonym.normalized.includes(normalizedHeader.normalized)

        if (isSubstring && 0.5 > bestConfidence) {
          bestConfidence = 0.5
          bestReason = `Contains: "${synonym}"`
        }
      }
    }

    return { confidence: bestConfidence, reason: bestReason }
  }

  /**
   * Normalize header names for better matching
   */
  private static normalizeHeader(header: string): NormalizedHeader {
    let normalized = header.toLowerCase()
      .trim()
      .replace(/[_\-\s]+/g, ' ')
      .replace(/[^\w\såäö]/g, '')
      .trim()

    // Remove accents/diacritics
    normalized = normalized
      .replace(/[àáâãäå]/g, 'a')
      .replace(/[èéêë]/g, 'e')
      .replace(/[ìíîï]/g, 'i')
      .replace(/[òóôõö]/g, 'o')
      .replace(/[ùúûü]/g, 'u')
      .replace(/[ýÿ]/g, 'y')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')

    const tokens = normalized.split(' ').filter(token => token.length > 0)

    return {
      original: header,
      normalized,
      tokens
    }
  }

  /**
   * Calculate similarity using Levenshtein distance
   */
  private static calculateSimilarity(str1: string, str2: string): number {
    if (str1 === str2) return 1.0

    const maxLength = Math.max(str1.length, str2.length)
    if (maxLength === 0) return 1.0

    const distance = this.levenshteinDistance(str1, str2)
    return Math.max(0, 1 - (distance / maxLength))
  }

  /**
   * Calculate Levenshtein distance
   */
  private static levenshteinDistance(str1: string, str2: string): number {
    const matrix: number[][] = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          )
        }
      }
    }

    return matrix[str2.length][str1.length]
  }

  /**
   * Calculate token-based matching score
   */
  private static calculateTokenMatch(tokens1: string[], tokens2: string[]): number {
    if (tokens1.length === 0 && tokens2.length === 0) return 1.0
    if (tokens1.length === 0 || tokens2.length === 0) return 0.0

    let matches = 0
    const maxTokens = Math.max(tokens1.length, tokens2.length)

    for (const token1 of tokens1) {
      for (const token2 of tokens2) {
        if (token1 === token2 || this.calculateSimilarity(token1, token2) > 0.8) {
          matches++
          break
        }
      }
    }

    return matches / maxTokens
  }

  /**
   * Validate and prepare column mappings for field mapping step
   * Consolidates validation logic used in the workflow
   */
  static validateAndPrepareMappings(
    identifierMapping: Record<string, string>,
    fieldMapping: Record<string, string>,
    headers: string[],
    platform: Platform,
    profileFieldMapping: Record<string, string> = {}
  ): {
    missingRequiredFields: string[]
    unmappedHeaders: string[]
    duplicateHeaders: string[]
    columnMapping: {
      identifierFields: Record<string, string>
      earningsFields: Record<string, string>
      profileFields: Record<string, string>
      confidence: number
      manualOverride: boolean
      unmappedHeaders: string[]
      missingRequiredFields: string[]
    }
  } {
    const platformConfig = this.getConfigForPlatform(platform)

    // Calculate missing required fields
    const requiredIdentifierFields = platformConfig?.requiredFields.identifierFields || []
    const requiredEarningsFields = platformConfig?.requiredFields.earningsFields || []
    const requiredProfileFields = platformConfig?.requiredFields.profileFields || []
    const missingIdentifierFields = requiredIdentifierFields.filter(field => {
      if (field === 'driverName') {
        const hasDriverName = !!identifierMapping.driverName
        const hasFirstLast =
          !!identifierMapping.firstName && !!identifierMapping.lastName
        return !(hasDriverName || hasFirstLast)
      }
      return !identifierMapping[field]
    })
    const missingEarningsFields = requiredEarningsFields.filter(
      field => !fieldMapping[field]
    )
    const missingProfileFields = requiredProfileFields.filter(
      field => !profileFieldMapping[field]
    )
    const missingRequiredFields = [
      ...missingIdentifierFields,
      ...missingEarningsFields,
      ...missingProfileFields
    ]

    // Calculate unmapped headers
    const allMappedHeaders = [
      ...Object.values(identifierMapping),
      ...Object.values(fieldMapping),
      ...Object.values(profileFieldMapping)
    ].filter(header => header !== '')
    const unmappedHeaders = headers.filter(header => !allMappedHeaders.includes(header))

    // Detect duplicate header usage across identifier/earnings mappings
    const duplicateHeaders = Array.from(new Set(
      allMappedHeaders.filter((header, index) => allMappedHeaders.indexOf(header) !== index)
    ))

    // Build column mapping object
    const columnMapping = {
      identifierFields: { ...identifierMapping },
      earningsFields: { ...fieldMapping },
      profileFields: { ...profileFieldMapping },
      confidence: missingRequiredFields.length === 0 ? 100 : 85,
      manualOverride: true,
      unmappedHeaders,
      missingRequiredFields
    }

    return {
      missingRequiredFields,
      unmappedHeaders,
      duplicateHeaders,
      columnMapping
    }
  }
}
